'use client';

import { Fragment, ReactNode } from 'react';
import { stegaClean } from '@sanity/client/stega';

import { IFooter, IHeader } from '@/sanity/lib/definitions';
import { IPageLink } from '@/sanity/globals/CTA';

type ShareNetwork = 'x' | 'facebook' | 'whatsapp' | 'linkedin';
type ProfileNetwork = 'instagram' | 'discord';

const SHARE_URL: Record<ShareNetwork, (url: string, title: string) => string> = {
  x: (url, title) =>
    `https://x.com/intent/tweet?url=${encodeURIComponent(url)}${title ? `&text=${encodeURIComponent(title)}` : ''}`,
  facebook: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  whatsapp: (url, title) => `https://wa.me/?text=${encodeURIComponent(title ? `${title} ${url}` : url)}`,
  linkedin: (url) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
};

const PROFILE_HOST: Record<ProfileNetwork, RegExp> = {
  instagram: /(^|\.)instagram\.com$/i,
  discord: /(^|\.)discord\.(gg|com)$/i,
};

const LABEL: Record<string, Record<ShareNetwork | ProfileNetwork | 'rail', string>> = {
  en: {
    rail: 'Share this article',
    x: 'Share on X',
    facebook: 'Share on Facebook',
    whatsapp: 'Share on WhatsApp',
    linkedin: 'Share on LinkedIn',
    instagram: 'IQ Capital on Instagram',
    discord: 'IQ Capital on Discord',
  },
  de: {
    rail: 'Artikel teilen',
    x: 'Auf X teilen',
    facebook: 'Auf Facebook teilen',
    whatsapp: 'Auf WhatsApp teilen',
    linkedin: 'Auf LinkedIn teilen',
    instagram: 'IQ Capital auf Instagram',
    discord: 'IQ Capital auf Discord',
  },
  br: {
    rail: 'Compartilhar artigo',
    x: 'Compartilhar no X',
    facebook: 'Compartilhar no Facebook',
    whatsapp: 'Compartilhar no WhatsApp',
    linkedin: 'Compartilhar no LinkedIn',
    instagram: 'IQ Capital no Instagram',
    discord: 'IQ Capital no Discord',
  },
};

function findProfileUrl(network: ProfileNetwork, footer?: IFooter, header?: IHeader) {
  const links: (IPageLink | undefined)[] = [
    ...(footer?.navColumns ?? []).flatMap((col) => (col.links ?? []).map((item) => item.link)),
    header?.headerDiscordCTA?.cta?.link,
  ];

  for (const link of links) {
    const url = stegaClean(link?.url)?.trim();
    if (!link?.externalLink || !url) continue;
    try {
      if (PROFILE_HOST[network].test(new URL(url).hostname)) return url;
    } catch {
    }
  }
  return undefined;
}

const ICON: Record<ShareNetwork | ProfileNetwork, ReactNode> = {
  x: (
    <svg viewBox="0 0 24 24" fill="none" className="h-[3rem] w-[3rem]">
      <path
        d="M4 20L10.768 13.232M13.228 10.772L20 4M4 4L15.733 20H20L8.267 4H4Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  facebook: (
    <svg viewBox="0 0 24 24" fill="none" className="h-[3rem] w-[3rem]">
      <path
        d="M12 2.03998C6.5 2.03998 2 6.52998 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.84998C10.44 7.33998 11.93 5.95998 14.22 5.95998C15.31 5.95998 16.45 6.14998 16.45 6.14998V8.61998H15.19C13.95 8.61998 13.56 9.38998 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96C15.9164 21.5878 18.0622 20.3855 19.6099 18.57C21.1576 16.7546 22.0054 14.4456 22 12.06C22 6.52998 17.5 2.03998 12 2.03998Z"
        fill="currentColor"
      />
    </svg>
  ),
  instagram: (
    <svg viewBox="0 0 22 22" fill="none" className="h-[2.75rem] w-[2.75rem]">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11 16.5C14.0376 16.5 16.5 14.0376 16.5 11C16.5 7.96243 14.0376 5.5 11 5.5C7.96243 5.5 5.5 7.96243 5.5 11C5.5 14.0376 7.96243 16.5 11 16.5ZM11 14.6667C13.025 14.6667 14.6667 13.025 14.6667 11C14.6667 8.97496 13.025 7.33333 11 7.33333C8.97496 7.33333 7.33333 8.97496 7.33333 11C7.33333 13.025 8.97496 14.6667 11 14.6667Z"
        fill="currentColor"
      />
      <path
        d="M16.5007 4.58337C15.9944 4.58337 15.584 4.99378 15.584 5.50004C15.584 6.0063 15.9944 6.41671 16.5007 6.41671C17.0069 6.41671 17.4173 6.0063 17.4173 5.50004C17.4173 4.99378 17.0069 4.58337 16.5007 4.58337Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.51548 3.91968C0.916016 5.0962 0.916016 6.63634 0.916016 9.71663V12.2833C0.916016 15.3636 0.916016 16.9038 1.51548 18.0802C2.04278 19.1151 2.88417 19.9565 3.91907 20.4838C5.09558 21.0833 6.63573 21.0833 9.71602 21.0833H12.2827C15.363 21.0833 16.9031 21.0833 18.0796 20.4838C19.1145 19.9565 19.9559 19.1151 20.4832 18.0802C21.0827 16.9038 21.0827 15.3636 21.0827 12.2833V9.71663C21.0827 6.63634 21.0827 5.0962 20.4832 3.91968C19.9559 2.88478 19.1145 2.04339 18.0796 1.51609C16.9031 0.916626 15.363 0.916626 12.2827 0.916626H9.71602C6.63573 0.916626 5.09558 0.916626 3.91907 1.51609C2.88417 2.04339 2.04278 2.88478 1.51548 3.91968ZM12.2827 2.74996H9.71602C8.14562 2.74996 7.07808 2.75139 6.25292 2.8188C5.44915 2.88447 5.03812 3.0035 4.75139 3.1496C4.06146 3.50114 3.50053 4.06207 3.14899 4.752C3.00289 5.03873 2.88386 5.44976 2.81819 6.25353C2.75078 7.07869 2.74935 8.14623 2.74935 9.71663V12.2833C2.74935 13.8537 2.75078 14.9212 2.81819 15.7464C2.88386 16.5502 3.00289 16.9612 3.14899 17.248C3.50053 17.9378 4.06146 18.4987 4.75139 18.8503C5.03812 18.9964 5.44915 19.1155 6.25292 19.1811C7.07808 19.2485 8.14562 19.25 9.71602 19.25H12.2827C13.8531 19.25 14.9206 19.2485 15.7458 19.1811C16.5496 19.1155 16.9606 18.9964 17.2473 18.8503C17.9372 18.4987 18.4981 17.9378 18.8497 17.248C18.9958 16.9612 19.1149 16.5502 19.1805 15.7464C19.2479 14.9212 19.2493 13.8537 19.2493 12.2833V9.71663C19.2493 8.14623 19.2479 7.07869 19.1805 6.25353C19.1149 5.44976 18.9958 5.03873 18.8497 4.752C18.4981 4.06207 17.9372 3.50114 17.2473 3.1496C16.9606 3.0035 16.5496 2.88447 15.7458 2.8188C14.9206 2.75139 13.8531 2.74996 12.2827 2.74996Z"
        fill="currentColor"
      />
    </svg>
  ),
  whatsapp: (
    <svg viewBox="0 0 24 24" fill="none" className="h-[3rem] w-[3rem]">
      <path
        d="M9 10C9 10.1326 9.05268 10.2598 9.14645 10.3536C9.24021 10.4473 9.36739 10.5 9.5 10.5C9.63261 10.5 9.75979 10.4473 9.85355 10.3536C9.94732 10.2598 10 10.1326 10 10V9C10 8.86739 9.94732 8.74021 9.85355 8.64645C9.75979 8.55268 9.63261 8.5 9.5 8.5C9.36739 8.5 9.24021 8.55268 9.14645 8.64645C9.05268 8.74021 9 8.86739 9 9V10ZM9 10C9 11.3261 9.52678 12.5979 10.4645 13.5355C11.4021 14.4732 12.6739 15 14 15M14 15H15C15.1326 15 15.2598 14.9473 15.3536 14.8536C15.4473 14.7598 15.5 14.6326 15.5 14.5C15.5 14.3674 15.4473 14.2402 15.3536 14.1464C15.2598 14.0527 15.1326 14 15 14H14C13.8674 14 13.7402 14.0527 13.6464 14.1464C13.5527 14.2402 13.5 14.3674 13.5 14.5C13.5 14.6326 13.5527 14.7598 13.6464 14.8536C13.7402 14.9473 13.8674 15 14 15ZM3 21L4.65 17.2C3.38766 15.408 2.82267 13.217 3.06104 11.0381C3.29942 8.85915 4.32479 6.84211 5.94471 5.36549C7.56463 3.88887 9.66775 3.05418 11.8594 3.01807C14.051 2.98195 16.1805 3.7469 17.8482 5.16934C19.5159 6.59179 20.6071 8.57395 20.9172 10.7438C21.2272 12.9137 20.7347 15.1222 19.5321 16.9547C18.3295 18.7873 16.4994 20.118 14.3854 20.6971C12.2713 21.2762 10.0186 21.0639 8.05 20.1L3 21Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  discord: (
    <svg viewBox="0 0 22 17.0301" fill="none" className="h-[2.75rem] w-[2.75rem]">
      <path
        d="M18.6361 1.42627C17.212 0.75997 15.6893 0.27573 14.0973 0C13.9018 0.353477 13.6734 0.82891 13.5159 1.20712C11.8236 0.952616 10.1469 0.952616 8.48573 1.20712C8.32828 0.82891 8.09467 0.353477 7.89741 0C6.30374 0.27573 4.77928 0.761749 3.35518 1.4298C0.482756 5.7704 -0.29591 10.0032 0.0934236 14.1759C1.99856 15.5986 3.84487 16.4628 5.66003 17.0284C6.1082 16.4116 6.50791 15.7559 6.85224 15.0649C6.19644 14.8157 5.56832 14.5082 4.97482 14.1512C5.13227 14.0345 5.28628 13.9125 5.43508 13.7871C9.05501 15.4802 12.9882 15.4802 16.5648 13.7871C16.7154 13.9125 16.8694 14.0345 17.0251 14.1512C16.4299 14.5099 15.8 14.8174 15.1442 15.0666C15.4885 15.7559 15.8865 16.4134 16.3364 17.0301C18.1533 16.4646 20.0014 15.6004 21.9065 14.1759C22.3633 9.33867 21.1261 5.14476 18.6361 1.42627ZM7.34541 11.6097C6.25874 11.6097 5.36759 10.5952 5.36759 9.35987C5.36759 8.1245 6.23972 7.10828 7.34541 7.10828C8.45114 7.10828 9.34226 8.12272 9.32323 9.35987C9.32495 10.5952 8.45114 11.6097 7.34541 11.6097ZM14.6545 11.6097C13.5678 11.6097 12.6767 10.5952 12.6767 9.35987C12.6767 8.1245 13.5488 7.10828 14.6545 7.10828C15.7602 7.10828 16.6514 8.12272 16.6323 9.35987C16.6323 10.5952 15.7602 11.6097 14.6545 11.6097Z"
        fill="currentColor"
      />
    </svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" fill="none" className="h-[3rem] w-[3rem]">
      <path
        d="M0 1.71919C0 0.77025 0.794437 0 1.77375 0H22.2263C23.2059 0 24 0.77025 24 1.71919V22.2811C24 23.2303 23.2059 24 22.2263 24H1.77375C0.794531 24 0 23.2304 0 22.2814V1.71891V1.71919Z"
        fill="currentColor"
      />
      <path
        d="M7.29337 20.0848V9.27994H3.702V20.0848H7.29375H7.29337ZM5.49844 7.80497C6.75056 7.80497 7.53009 6.97528 7.53009 5.93841C7.50666 4.87791 6.75056 4.07138 5.52225 4.07138C4.29309 4.07138 3.49031 4.87791 3.49031 5.93831C3.49031 6.97519 4.26956 7.80488 5.47491 7.80488H5.49816L5.49844 7.80497ZM9.28125 20.0848H12.8723V14.0515C12.8723 13.729 12.8958 13.4057 12.9907 13.1753C13.2502 12.5299 13.8411 11.8617 14.8334 11.8617C16.1326 11.8617 16.6526 12.8525 16.6526 14.3051V20.0848H20.2436V13.8896C20.2436 10.571 18.4721 9.02663 16.1093 9.02663C14.1722 9.02663 13.3214 10.1093 12.8487 10.8468H12.8726V9.28031H9.28144C9.32831 10.2939 9.28116 20.0852 9.28116 20.0852L9.28125 20.0848Z"
        fill="white"
      />
    </svg>
  ),
};

const ORDER: (ShareNetwork | ProfileNetwork)[] = [
  'x',
  'facebook',
  'instagram',
  'whatsapp',
  'discord',
  'linkedin',
];

const itemClassName =
  '-my-[1rem] flex w-full items-center justify-center py-[1rem] text-[#103037] transition-colors hover:text-[#0b6d51]';

export default function BlogShareRail({
  title,
  footer,
  header,
  language = 'en',
  className,
}: {
  title?: string;
  footer?: IFooter;
  header?: IHeader;
  language?: string;
  className?: string;
}) {
  const labels = LABEL[stegaClean(language) ?? ''] ?? LABEL.en;

  const share = (network: ShareNetwork) => {
    const url = `${window.location.origin}${window.location.pathname}`;
    window.open(SHARE_URL[network](url, stegaClean(title) ?? ''), '_blank', 'noopener,noreferrer');
  };

  const profiles: Record<ProfileNetwork, string | undefined> = {
    instagram: findProfileUrl('instagram', footer, header),
    discord: findProfileUrl('discord', footer, header),
  };

  const renderItem = (network: ShareNetwork | ProfileNetwork) => {
    if (network === 'instagram' || network === 'discord') {
      const href = profiles[network];
      if (!href) return null;
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={labels[network]}
          className={itemClassName}
        >
          {ICON[network]}
        </a>
      );
    }

    const shareNetwork = network;
    return (
      <button
        type="button"
        onClick={() => share(shareNetwork)}
        aria-label={labels[network]}
        className={itemClassName}
      >
        {ICON[network]}
      </button>
    );
  };

  const items = ORDER.map((network) => ({ network, node: renderItem(network) })).filter(
    (item) => item.node,
  );

  return (
    <div className={className}>
      <div
        role="group"
        aria-label={labels.rail}
        className="sticky top-[12rem] flex w-[6.625rem] flex-col items-center gap-[1.89rem] border border-[#103037]/10 pb-[2.14rem] pt-[2.465rem]"
      >
        {items.map(({ network, node }, index) => (
          <Fragment key={network}>
            {index > 0 && (
              <span aria-hidden="true" className="h-px w-full shrink-0 bg-[#103037]/10" />
            )}
            {node}
          </Fragment>
        ))}
      </div>
    </div>
  );
}
